import React from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Typography,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e9ecef",
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  headerTitle: {
    color: "#495057",
    fontWeight: 600,
    marginLeft: theme.spacing(1),
    fontSize: "1rem",
  },
  searchField: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#f8f9fa",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover fieldset": {
        borderColor: "#F97B18",
        borderWidth: "1px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F97B18",
        borderWidth: "1px",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
      },
    },
  },
  formControl: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#f8f9fa",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover fieldset": {
        borderColor: "#F97B18",
        borderWidth: "1px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F97B18",
        borderWidth: "1px",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
      },
    },
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
  clearButton: {
    color: "#6c757d",
    borderColor: "#dee2e6",
    fontSize: "0.8rem",
    padding: "6px 12px",
    borderRadius: "6px",
    textTransform: "none",
    fontWeight: 500,
    borderWidth: "1px",
    "&:hover": {
      backgroundColor: "#f8f9fa",
      borderColor: "#adb5bd",
      color: "#495057",
    },
  },
  applyButton: {
    backgroundColor: "#F97B18",
    color: "#fff",
    fontSize: "0.8rem",
    padding: "6px 16px",
    borderRadius: "6px",
    textTransform: "none",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#D05D02",
      boxShadow: "0 2px 4px rgba(249, 123, 24, 0.2)",
    },
  },
  activeFilters: {
    marginTop: theme.spacing(1.5),
  },
  filterChip: {
    margin: theme.spacing(0.25),
    fontSize: "0.75rem",
    height: "24px",
    borderRadius: "12px",
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "1px solid #bbdefb",
    fontWeight: 500,
    "& .MuiChip-deleteIcon": {
      color: "#1976d2",
      fontSize: "16px",
    },
    "&:hover": {
      backgroundColor: "#bbdefb",
      borderColor: "#90caf9",
    },
  },
}));

const Filters = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  categoryOptions = [],
  loading = false,
  statusOptions = [],
}) => {
  const classes = useStyles();

  const handleFilterChange = (field, value) => {
    onFiltersChange({
      ...filters,
      [field]: value,
    });
  };

  const handleClearFilter = (field) => {
    const newFilters = { ...filters };
    delete newFilters[field];
    onFiltersChange(newFilters);
  };

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => 
      filters[key] && filters[key] !== "" && key !== "sortBy" && key !== "sortOrder"
    ).length;
  };

  const hasActiveFilters = getActiveFiltersCount() > 0;

  return (
    <div className={classes.root}>
      {/* Header compacto */}
      <div className={classes.header}>
        <FilterIcon style={{ color: "#F97B18", fontSize: 18 }} />
        <Typography className={classes.headerTitle}>
          Filtros
        </Typography>
        {hasActiveFilters && (
          <Chip
            label={`${getActiveFiltersCount()}`}
            size="small"
            style={{ 
              marginLeft: 8,
              backgroundColor: "#F97B18",
              color: "#fff",
              fontSize: "0.7rem",
              height: "20px",
              borderRadius: "10px",
              fontWeight: 600
            }}
          />
        )}
      </div>

             {/* Layout compacto em linha */}
       <Grid container spacing={2} alignItems="center">
         {/* Busca */}
         <Grid item xs={12} sm={4}>
           <TextField
             fullWidth
             size="small"
             label="Buscar tickets"
             placeholder="Título, descrição, usuário..."
             value={filters.keyword || ""}
             onChange={(e) => handleFilterChange("keyword", e.target.value)}
             className={classes.searchField}
             InputProps={{
               startAdornment: <SearchIcon style={{ marginRight: 8, color: "#6c757d", fontSize: 18 }} />,
             }}
           />
         </Grid>

         {/* Status */}
         <Grid item xs={12} sm={3}>
           <FormControl fullWidth size="small" className={classes.formControl}>
             <InputLabel>Status</InputLabel>
                         <Select
              value={filters.status?.id || filters.status?.value || ""}
              onChange={(e) => {
                const selectedOption = statusOptions.find(opt => opt.id === e.target.value || opt.value === e.target.value);
                handleFilterChange("status", selectedOption || e.target.value);
              }}
            >
              <MenuItem value="">Todos os status</MenuItem>
              {statusOptions?.map((option) => (
                <MenuItem key={option.id || option.value} value={option.id || option.value}>
                  {option.status || option.value || option.label}
                </MenuItem>
              ))}
             </Select>
           </FormControl>
         </Grid>

         {/* Categoria */}
         <Grid item xs={12} sm={3}>
           <FormControl fullWidth size="small" className={classes.formControl}>
             <InputLabel>Categoria</InputLabel>
             <Select
               value={filters.category?.value || ""}
               onChange={(e) => {
                 const selectedOption = categoryOptions.find(opt => opt.value === e.target.value);
                 handleFilterChange("category", selectedOption || e.target.value);
               }}
             >
               <MenuItem value="">Todas as categorias</MenuItem>
               {categoryOptions.map((option) => (
                 <MenuItem key={option.value} value={option.value}>
                   {option.label}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>
         </Grid>

         {/* Botões de Ação */}
         <Grid item xs={12} sm={2}>
           <div className={classes.actionButtons}>
             <Button
               variant="outlined"
               onClick={onClearFilters}
               disabled={!hasActiveFilters || loading}
               className={classes.clearButton}
               startIcon={<ClearIcon style={{ fontSize: 16 }} />}
               size="small"
             >
               Limpar
             </Button>
             <Button
               variant="contained"
               onClick={onApplyFilters}
               disabled={loading}
               className={classes.applyButton}
               startIcon={<SearchIcon style={{ fontSize: 16 }} />}
               size="small"
             >
               {loading ? "..." : "Atualizar"}
             </Button>
           </div>
         </Grid>
       </Grid>

      {/* Filtros Ativos */}
      {hasActiveFilters && (
        <div className={classes.activeFilters}>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            Filtros ativos:
          </Typography>
          <Box display="flex" flexWrap="wrap">
            {filters.keyword && (
              <Chip
                label={`Busca: "${filters.keyword}"`}
                onDelete={() => handleClearFilter("keyword")}
                className={classes.filterChip}
                size="small"
              />
            )}
            {filters.status && (
              <Chip
                label={`Status: ${
                  typeof filters.status === 'object' 
                    ? (statusOptions.find(opt => opt.id === filters.status.id)?.status || filters.status.value || filters.status.status || 'Selecionado')
                    : filters.status
                }`}
                onDelete={() => handleClearFilter("status")}
                className={classes.filterChip}
                size="small"
              />
            )}
            {filters.category && (
              <Chip
                label={`Categoria: ${
                  typeof filters.category === 'object'
                    ? (categoryOptions.find(opt => opt.value === filters.category.value)?.label || filters.category.label || 'Selecionada')
                    : filters.category
                }`}
                onDelete={() => handleClearFilter("category")}
                className={classes.filterChip}
                size="small"
              />
            )}
          </Box>
        </div>
      )}
    </div>
  );
};

export default Filters; 